import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../api/supabase';

export default function ProfitLoss() {
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = today.slice(0, 7) + '-01';
  const [from, setFrom] = useState(firstOfMonth);
  const [to, setTo] = useState(today);
  const [income, setIncome] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState('');
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    supabase.from('branches').select('id, name, code').eq('is_active', true).order('name').then(({ data }) => setBranches(data || []));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    // Join straight through to journal_entries and page through results — an .in()
    // filter over a fetched-separately list of entry ids breaks once there are enough
    // entries to blow past the URL length limit (silently, since neither query used to
    // check for errors), which is exactly what was happening at 1,600+ entries.
    const PAGE = 1000;
    let lines = [], pageFrom = 0;
    while (true) {
      let q = supabase
        .from('journal_lines')
        .select('base_debit, base_credit, account:account_id(id, account_code, account_name, account_types(category)), journal_entries!inner(status, entry_date, branch_id)')
        .eq('journal_entries.status', 'POSTED')
        .gte('journal_entries.entry_date', from)
        .lte('journal_entries.entry_date', to);
      if (branchFilter) q = q.eq('journal_entries.branch_id', branchFilter);
      const { data, error } = await q.range(pageFrom, pageFrom + PAGE - 1);
      if (error) { console.error('ProfitLoss load error:', error); setLoadError(error.message); break; }
      if (!data?.length) break;
      lines = lines.concat(data);
      if (data.length < PAGE) break;
      pageFrom += PAGE;
    }

    const incMap = {}, expMap = {};
    (lines || []).forEach(l => {
      const acc = l.account;
      if (!acc) return;
      const cat = acc.account_types?.category;
      const dr = parseFloat(l.base_debit || 0);
      const cr = parseFloat(l.base_credit || 0);
      if (cat === 'INCOME') {
        if (!incMap[acc.account_code]) incMap[acc.account_code] = { id: acc.id, code: acc.account_code, name: acc.account_name, amount: 0 };
        incMap[acc.account_code].amount += (cr - dr); // income: CR normal
      }
      if (cat === 'EXPENSE') {
        if (!expMap[acc.account_code]) expMap[acc.account_code] = { id: acc.id, code: acc.account_code, name: acc.account_name, amount: 0 };
        expMap[acc.account_code].amount += (dr - cr); // expense: DR normal
      }
    });

    const inc = Object.values(incMap).sort((a, b) => a.code.localeCompare(b.code));
    const exp = Object.values(expMap).sort((a, b) => a.code.localeCompare(b.code));
    setIncome(inc);
    setExpenses(exp);
    setLoading(false);
  }, [from, to, branchFilter]);

  useEffect(() => { load(); }, [load]);

  const totalIncome = income.reduce((s, r) => s + r.amount, 0);
  const totalExpenses = expenses.reduce((s, r) => s + r.amount, 0);
  const netProfit = totalIncome - totalExpenses;

  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">Profit & Loss</div><div className="page-subtitle">Income vs Expenses for the period</div></div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
          <div>
            <label className="form-label">Branch</label>
            <select className="form-input" value={branchFilter} onChange={e => setBranchFilter(e.target.value)}>
              <option value="">All Branches</option>
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div><label className="form-label">From</label><input className="form-input" type="date" value={from} onChange={e => setFrom(e.target.value)} /></div>
          <div><label className="form-label">To</label><input className="form-input" type="date" value={to} onChange={e => setTo(e.target.value)} /></div>
          <button className="btn btn-secondary" onClick={() => window.print()}>Print</button>
        </div>
      </div>
      {loadError && <div style={{ color: 'var(--danger)', marginBottom: 16, padding: '10px 14px', background: 'rgba(239,68,68,0.06)', borderRadius: 7 }}>Failed to load: {loadError}</div>}

      <div className="stats-grid" style={{ marginBottom: 20 }}>
        <div className="stat-card"><div className="stat-label">Total Income</div><div className="stat-value amount" style={{ color: 'var(--success)' }}>{totalIncome.toLocaleString()}</div></div>
        <div className="stat-card"><div className="stat-label">Total Expenses</div><div className="stat-value amount" style={{ color: 'var(--danger)' }}>{totalExpenses.toLocaleString()}</div></div>
        <div className="stat-card"><div className="stat-label">Net Profit / (Loss)</div><div className="stat-value amount" style={{ color: netProfit >= 0 ? 'var(--success)' : 'var(--danger)' }}>{netProfit.toLocaleString()}</div></div>
        <div className="stat-card"><div className="stat-label">Net Margin</div><div className="stat-value" style={{ color: netProfit >= 0 ? 'var(--success)' : 'var(--danger)' }}>{totalIncome > 0 ? ((netProfit / totalIncome) * 100).toFixed(1) + '%' : 'N/A'}</div></div>
      </div>

      {loading ? <div className="spinner" /> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card">
            <div className="card-header">
              <div className="card-title">Income</div>
              <div className="amount" style={{ color: 'var(--success)', fontWeight: 700 }}>{totalIncome.toLocaleString()}</div>
            </div>
            <div className="table-wrapper">
              <table>
                <thead><tr><th>Code</th><th>Account</th><th style={{ textAlign: 'right' }}>Amount</th></tr></thead>
                <tbody>
                  {income.map(r => (
                    <tr key={r.code} onClick={() => navigate(`/accounts/${r.id}/ledger?from=${from}&to=${to}${branchFilter ? `&branch=${branchFilter}` : ''}`)} style={{ cursor: 'pointer' }}>
                      <td style={{ fontFamily: 'monospace', color: 'var(--text-muted)' }}>{r.code}</td>
                      <td>{r.name}</td>
                      <td style={{ textAlign: 'right', color: 'var(--success)' }} className="amount">{r.amount.toLocaleString()}</td>
                    </tr>
                  ))}
                  {income.length === 0 && <tr><td colSpan={3} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 16 }}>No income recorded</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title">Expenses</div>
              <div className="amount" style={{ color: 'var(--danger)', fontWeight: 700 }}>{totalExpenses.toLocaleString()}</div>
            </div>
            <div className="table-wrapper">
              <table>
                <thead><tr><th>Code</th><th>Account</th><th style={{ textAlign: 'right' }}>Amount</th></tr></thead>
                <tbody>
                  {expenses.map(r => (
                    <tr key={r.code} onClick={() => navigate(`/accounts/${r.id}/ledger?from=${from}&to=${to}${branchFilter ? `&branch=${branchFilter}` : ''}`)} style={{ cursor: 'pointer' }}>
                      <td style={{ fontFamily: 'monospace', color: 'var(--text-muted)' }}>{r.code}</td>
                      <td>{r.name}</td>
                      <td style={{ textAlign: 'right', color: 'var(--danger)' }} className="amount">{r.amount.toLocaleString()}</td>
                    </tr>
                  ))}
                  {expenses.length === 0 && <tr><td colSpan={3} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 16 }}>No expenses recorded</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card" style={{ borderColor: netProfit >= 0 ? 'var(--success)' : 'var(--danger)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
              <div style={{ fontWeight: 700, fontSize: 16 }}>Net {netProfit >= 0 ? 'Profit' : 'Loss'}</div>
              <div className="amount" style={{ fontSize: 24, fontWeight: 700, color: netProfit >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                {netProfit < 0 ? '(' : ''}{Math.abs(netProfit).toLocaleString()}{netProfit < 0 ? ')' : ''}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
